import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { generateReport } from "../api/endpoints/reports"
import {
  approveDisbursement,
  getDisbursement,
  holdDisbursement,
  listDisbursements,
  manualCompleteDisbursement,
  manualFailDisbursement,
  rejectDisbursement,
  retryDisbursement,
} from "../api/endpoints/loans"
import {
  Button,
  BusinessPageHeader,
  ContentCard,
  DataTable,
  Drawer,
  EmptyState,
  FieldList,
  FilterField,
  FilterToolbar,
  Input,
  MetricGrid,
  Modal,
  SegmentedTabs,
  Select,
  StatCard,
  StatusPill,
  StickyActionBar,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Textarea,
  Th,
} from "../components/ui"
import { formatCurrency, formatDateTime, formatEnumLabel } from "../utils/formatting"

type DisbursementTab =
  | "PENDING_APPROVAL"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "REJECTED"
  | "ALL"

type ActionKind =
  | "approve"
  | "hold"
  | "reject"
  | "retry"
  | "manual-complete"
  | "manual-fail"

const TAB_OPTIONS: DisbursementTab[] = [
  "PENDING_APPROVAL",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
  "REJECTED",
  "ALL",
]

function statusTone(status?: string): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "COMPLETED":
      return "success"
    case "FAILED":
    case "REJECTED":
      return "danger"
    case "PENDING_APPROVAL":
    case "HELD":
      return "warning"
    default:
      return "info"
  }
}

function priorityForRow(row: any) {
  if ((row.queueAgeMinutes ?? 0) >= 240 || (row.priorDefaultCount ?? 0) > 0) {
    return { label: "Level 3", tone: "danger" as const }
  }

  if ((row.queueAgeMinutes ?? 0) >= 120 || (row.riskFlags ?? []).length >= 2) {
    return { label: "Level 2", tone: "warning" as const }
  }

  return { label: "Level 1", tone: "info" as const }
}

function queueAgeText(minutes?: number | null) {
  if (!minutes || minutes <= 0) {
    return "Fresh"
  }

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }

  return `${minutes}m`
}

function queueAgeClass(minutes?: number | null) {
  if ((minutes ?? 0) >= 240) {
    return "text-red-700"
  }

  if ((minutes ?? 0) >= 120) {
    return "text-amber-700"
  }

  return "text-slate-600"
}

function toMinorUnits(value: string | null) {
  if (!value) {
    return undefined
  }

  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined
  }

  return Math.round(parsed * 100)
}

function canRunAction(detail: any, action: ActionKind) {
  const status = detail?.status
  if (!status) {
    return false
  }

  if (action === "approve") {
    return ["PENDING_APPROVAL", "HELD", "FAILED"].includes(status)
  }

  if (action === "hold") {
    return status === "PENDING_APPROVAL"
  }

  if (action === "reject") {
    return ["PENDING_APPROVAL", "HELD"].includes(status)
  }

  if (action === "retry") {
    return ["FAILED", "HELD"].includes(status)
  }

  if (action === "manual-complete") {
    return !["COMPLETED", "REJECTED"].includes(status)
  }

  return !["FAILED", "COMPLETED", "REJECTED"].includes(status)
}

function actionTitle(action: ActionKind) {
  switch (action) {
    case "approve":
      return "Authorize Disbursement"
    case "hold":
      return "Hold For Review"
    case "reject":
      return "Reject Disbursement"
    case "retry":
      return "Retry Disbursement"
    case "manual-complete":
      return "Mark As Completed"
    default:
      return "Mark As Failed"
  }
}

function actionDescription(action: ActionKind) {
  switch (action) {
    case "approve":
      return "Funds will be released to the selected destination. This action requires an audit note."
    case "hold":
      return "Move this request into a held state while additional review is completed."
    case "reject":
      return "Reject this request and capture a clear rejection reason for audit review."
    case "retry":
      return "Retry provider processing while preserving the failure history."
    case "manual-complete":
      return "Use only when provider confirmation exists outside the automatic processing flow."
    default:
      return "Use only when a confirmed provider or operational failure has occurred."
  }
}

function tabLabel(tab: DisbursementTab) {
  switch (tab) {
    case "PENDING_APPROVAL":
      return "Pending Authorization"
    case "PROCESSING":
      return "In Progress"
    case "COMPLETED":
      return "Completed"
    case "FAILED":
      return "Failed"
    case "REJECTED":
      return "Rejected"
    default:
      return "All"
  }
}

function tabSubtitle(tab: DisbursementTab) {
  switch (tab) {
    case "PENDING_APPROVAL":
      return "Review aging, KYC state, and risk context before authorizing funds."
    case "PROCESSING":
      return "Monitor provider progress and investigate delayed settlements."
    case "COMPLETED":
      return "Review successful disbursements within the current filter scope."
    case "FAILED":
      return "Investigate failed payouts and route retries or recovery actions."
    case "REJECTED":
      return "Review rejected requests and the audit rationale captured for each decision."
    default:
      return "Inspect all disbursement requests within the current filter scope."
  }
}

function emptyStateCopy(tab: DisbursementTab) {
  switch (tab) {
    case "PENDING_APPROVAL":
      return {
        title: "No pending authorizations in the current filter scope",
        description: "Adjust the filters or switch tabs to inspect another part of the queue.",
      }
    case "PROCESSING":
      return {
        title: "No in-progress disbursements match the current view",
        description: "Adjust the filters or switch tabs to inspect another part of the queue.",
      }
    case "COMPLETED":
      return {
        title: "No completed disbursements match the current view",
        description: "Adjust the filters or switch tabs to inspect another part of the queue.",
      }
    case "FAILED":
      return {
        title: "No failed disbursements match the current view",
        description: "Adjust the filters or switch tabs to inspect another part of the queue.",
      }
    case "REJECTED":
      return {
        title: "No rejected disbursements match the current view",
        description: "Adjust the filters or switch tabs to inspect another part of the queue.",
      }
    default:
      return {
        title: "No disbursements match the current view",
        description: "Adjust the tab or filters to inspect another part of the queue.",
      }
  }
}

export function DisbursementsPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [actionState, setActionState] = useState<{
    action: ActionKind
    disbursementId: string
    detail: any
  } | null>(null)
  const [auditNote, setAuditNote] = useState("")
  const [reason, setReason] = useState("")
  const [failureReason, setFailureReason] = useState("")
  const [transactionRef, setTransactionRef] = useState("")
  const [simulateResult, setSimulateResult] = useState("PROCESSING")

  const activeTab = (searchParams.get("tab") as DisbursementTab) || "PENDING_APPROVAL"
  const selectedId = searchParams.get("selected_disbursement_id")

  const queryParams = useMemo(() => {
    return {
      ...(activeTab !== "ALL" ? { status: activeTab } : {}),
      ...(searchParams.get("search") ? { search: searchParams.get("search")! } : {}),
      ...(searchParams.get("loan_type") ? { loan_type: searchParams.get("loan_type")! } : {}),
      ...(searchParams.get("product_name")
        ? { product_name: searchParams.get("product_name")! }
        : {}),
      ...(searchParams.get("payment_method")
        ? { payment_method: searchParams.get("payment_method")! }
        : {}),
      ...(searchParams.get("kyc_status") ? { kyc_status: searchParams.get("kyc_status")! } : {}),
      ...(searchParams.get("region") ? { region: searchParams.get("region")! } : {}),
      ...(toMinorUnits(searchParams.get("amount_min")) !== undefined
        ? { amount_min: String(toMinorUnits(searchParams.get("amount_min"))) }
        : {}),
      ...(toMinorUnits(searchParams.get("amount_max")) !== undefined
        ? { amount_max: String(toMinorUnits(searchParams.get("amount_max"))) }
        : {}),
      ...(searchParams.get("created_from")
        ? { created_from: `${searchParams.get("created_from")}T00:00:00Z` }
        : {}),
      ...(searchParams.get("created_to")
        ? { created_to: `${searchParams.get("created_to")}T23:59:59Z` }
        : {}),
      per_page: "50",
    }
  }, [activeTab, searchParams])

  const listQuery = useQuery({
    queryKey: ["disbursements", queryParams],
    queryFn: () => listDisbursements(queryParams),
  })

  const detailQuery = useQuery({
    queryKey: ["disbursement-detail", selectedId],
    queryFn: () => getDisbursement(selectedId!),
    enabled: Boolean(selectedId),
  })

  const exportMutation = useMutation({
    mutationFn: (format: "csv" | "pdf") =>
      generateReport({
        templateKey: "DISBURSEMENT_SUMMARY",
        format,
        delivery: "download",
        filters: queryParams,
      }),
  })

  const actionMutation = useMutation({
    mutationFn: async (payload: { action: ActionKind; disbursementId: string }) => {
      if (payload.action === "approve") {
        return approveDisbursement(payload.disbursementId, {
          auditNote,
          simulateResult,
        })
      }

      if (payload.action === "hold") {
        return holdDisbursement(payload.disbursementId, {
          auditNote,
          reason,
        })
      }

      if (payload.action === "reject") {
        return rejectDisbursement(payload.disbursementId, {
          auditNote,
          reason,
        })
      }

      if (payload.action === "retry") {
        return retryDisbursement(payload.disbursementId, {
          auditNote,
          simulateResult,
        })
      }

      if (payload.action === "manual-complete") {
        return manualCompleteDisbursement(payload.disbursementId, {
          auditNote,
          transactionRef,
        })
      }

      return manualFailDisbursement(payload.disbursementId, {
        auditNote,
        failureReason,
      })
    },
    onSuccess: async () => {
      setActionState(null)
      setAuditNote("")
      setReason("")
      setFailureReason("")
      setTransactionRef("")
      setSimulateResult("PROCESSING")
      await queryClient.invalidateQueries({ queryKey: ["disbursements"] })
      if (selectedId) {
        await queryClient.invalidateQueries({ queryKey: ["disbursement-detail", selectedId] })
      }
    },
  })

  const rows = listQuery.data?.data ?? []
  const summary = listQuery.data?.meta?.summary
  const detail = detailQuery.data?.data
  const emptyState = emptyStateCopy(activeTab)
  const totalCount =
    (summary?.pendingCount ?? 0) +
    (summary?.processingCount ?? 0) +
    (summary?.completedCount ?? 0) +
    (summary?.failedCount ?? 0) +
    (summary?.heldCount ?? 0) +
    (summary?.rejectedCount ?? 0)

  const tabOptions = [
    {
      value: "PENDING_APPROVAL" as const,
      label: tabLabel("PENDING_APPROVAL"),
      count: summary?.pendingCount ?? 0,
      tone: "warning" as const,
    },
    {
      value: "PROCESSING" as const,
      label: tabLabel("PROCESSING"),
      count: summary?.processingCount ?? 0,
      tone: "info" as const,
    },
    {
      value: "COMPLETED" as const,
      label: tabLabel("COMPLETED"),
      count: summary?.completedCount ?? 0,
      tone: "neutral" as const,
    },
    {
      value: "FAILED" as const,
      label: tabLabel("FAILED"),
      count: summary?.failedCount ?? 0,
      tone: "danger" as const,
    },
    {
      value: "REJECTED" as const,
      label: tabLabel("REJECTED"),
      count: summary?.rejectedCount ?? 0,
      tone: "neutral" as const,
    },
    {
      value: "ALL" as const,
      label: tabLabel("ALL"),
      count: totalCount,
      tone: "neutral" as const,
    },
  ]

  function updateParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    setSearchParams(next)
  }

  function openDetail(disbursementId: string) {
    updateParam("selected_disbursement_id", disbursementId)
  }

  function openAction(action: ActionKind, record: any) {
    setActionState({
      action,
      disbursementId: record.disbursementId,
      detail: record,
    })
    setAuditNote(record.reviewNote ?? "")
    setReason(record.holdReason ?? record.rejectedReason ?? "")
    setFailureReason(record.failedReason ?? "")
    setTransactionRef(record.transactionRef ?? "")
    setSimulateResult("PROCESSING")
  }

  return (
    <div className="space-y-6">
      <BusinessPageHeader
        eyebrow="Super Admin Control"
        title="Disbursements"
        subtitle="Loan disbursement authorization and monitoring. Review queue pressure, inspect KYC and terms, then move requests through a controlled approve, hold, reject, or recovery flow."
        meta={
          <>
            <StatusPill tone="warning">{summary?.pendingCount ?? 0} pending authorization</StatusPill>
            {(summary?.heldCount ?? 0) > 0 ? (
              <StatusPill tone="warning">{summary?.heldCount ?? 0} held for review</StatusPill>
            ) : null}
            {(summary?.failedCount ?? 0) > 0 ? (
              <StatusPill tone="danger">{summary?.failedCount ?? 0} failures need follow-up</StatusPill>
            ) : null}
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() =>
                void queryClient.invalidateQueries({
                  queryKey: ["disbursements"],
                })
              }
            >
              Refresh Queue
            </Button>
            <Button
              variant="outline"
              disabled={exportMutation.isPending}
              onClick={() => exportMutation.mutate("csv")}
            >
              Export CSV
            </Button>
            <Button disabled={exportMutation.isPending} onClick={() => exportMutation.mutate("pdf")}>
              Export PDF
            </Button>
          </>
        }
      />

      <MetricGrid columns="xl:grid-cols-6">
        <StatCard
          label="Pending Authorization"
          value={summary?.pendingCount ?? 0}
          helper={`${formatCurrency(summary?.pendingAmount ?? 0)} across current filters`}
          tone="amber"
        />
        <StatCard
          label="Approved Today"
          value={summary?.approvedTodayCount ?? 0}
          helper={`${formatCurrency(summary?.approvedTodayAmount ?? 0)} across current filters`}
          tone="blue"
        />
        <StatCard
          label="Disbursed This Period"
          value={summary?.disbursedPeriodCount ?? 0}
          helper={`${formatCurrency(summary?.disbursedPeriodAmount ?? 0)} across current filters`}
          tone="emerald"
        />
        <StatCard
          label="Failed"
          value={summary?.failedCount ?? 0}
          helper={(summary?.failedCount ?? 0) > 0 ? "Investigate and retry or escalate." : "No failed items."}
          tone={(summary?.failedCount ?? 0) > 0 ? "red" : "slate"}
        />
        <StatCard
          label="Average Queue Time"
          value={queueAgeText(Math.round(summary?.averageQueueMinutes ?? 0))}
          helper="Pending authorizations across current filters"
        />
        <StatCard
          label="Average Processing Time"
          value={queueAgeText(Math.round(summary?.averageProcessingMinutes ?? 0))}
          helper="Approval to completion across current filters"
        />
      </MetricGrid>

      <SegmentedTabs<"ALL" | "STANDARD" | "MICRO">
        value={(searchParams.get("loan_type") as "STANDARD" | "MICRO" | null) ?? "ALL"}
        onChange={(value) => updateParam("loan_type", value === "ALL" ? null : value)}
        options={[
          { value: "ALL", label: "All Loans" },
          { value: "STANDARD", label: "Standard" },
          { value: "MICRO", label: "Micro", tone: "info" },
        ]}
      />

      <FilterToolbar>
        <FilterField label="Search" widthClassName="min-w-[220px] flex-1">
          <Input
            placeholder="Client, phone, loan, or disbursement ID"
            value={searchParams.get("search") ?? ""}
            onChange={(event) => updateParam("search", event.target.value || null)}
          />
        </FilterField>
        <FilterField label="Product">
          <Input
            placeholder="Product name"
            value={searchParams.get("product_name") ?? ""}
            onChange={(event) => updateParam("product_name", event.target.value || null)}
          />
        </FilterField>
        <FilterField label="Method">
          <Select
            value={searchParams.get("payment_method") ?? ""}
            onChange={(event) => updateParam("payment_method", event.target.value || null)}
          >
            <option value="">All methods</option>
            <option value="MOBILE_MONEY">Mobile Money</option>
            <option value="BANK_TRANSFER">Bank Transfer</option>
            <option value="CASH">Cash</option>
          </Select>
        </FilterField>
        <FilterField label="KYC">
          <Select
            value={searchParams.get("kyc_status") ?? ""}
            onChange={(event) => updateParam("kyc_status", event.target.value || null)}
          >
            <option value="">All KYC states</option>
            <option value="APPROVED">Approved</option>
            <option value="ACTION_REQUIRED">Action Required</option>
            <option value="PENDING">Pending</option>
            <option value="REJECTED">Rejected</option>
          </Select>
        </FilterField>
        <FilterField label="Region">
          <Input
            placeholder="Region"
            value={searchParams.get("region") ?? ""}
            onChange={(event) => updateParam("region", event.target.value || null)}
          />
        </FilterField>
        <FilterField label="Amount Min">
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={searchParams.get("amount_min") ?? ""}
            onChange={(event) => updateParam("amount_min", event.target.value || null)}
          />
        </FilterField>
        <FilterField label="Amount Max">
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={searchParams.get("amount_max") ?? ""}
            onChange={(event) => updateParam("amount_max", event.target.value || null)}
          />
        </FilterField>
        <FilterField label="Requested From">
          <Input
            type="date"
            value={searchParams.get("created_from") ?? ""}
            onChange={(event) => updateParam("created_from", event.target.value || null)}
          />
        </FilterField>
        <FilterField label="Requested To">
          <Input
            type="date"
            value={searchParams.get("created_to") ?? ""}
            onChange={(event) => updateParam("created_to", event.target.value || null)}
          />
        </FilterField>
        <div className="ml-auto">
          <Button
            variant="ghost"
            onClick={() => {
              const next = new URLSearchParams()
              next.set("tab", activeTab)
              setSearchParams(next)
            }}
          >
            Clear Filters
          </Button>
        </div>
      </FilterToolbar>

      <SegmentedTabs
        value={activeTab}
        options={tabOptions}
        onChange={(value) => updateParam("tab", value)}
      />

      <ContentCard
        title={tabLabel(activeTab)}
        subtitle={tabSubtitle(activeTab)}
      >
        {rows.length === 0 ? (
          <EmptyState
            title={emptyState.title}
            description={emptyState.description}
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <TableHead>
                <Th>Priority</Th>
                <Th>Client / Loan</Th>
                <Th>Terms</Th>
                <Th>KYC</Th>
                <Th>Method / Destination</Th>
                <Th>Requested</Th>
                <Th>Queue Age</Th>
                <Th>Risk Flags</Th>
                <Th className="text-right">Action</Th>
              </TableHead>
              <TableBody>
                {rows.map((row: any) => {
                  const priority = priorityForRow(row)
                  return (
                    <TableRow
                      key={row.disbursementId}
                      className={
                        row.status === "FAILED"
                          ? "border-l-4 border-red-500"
                          : row.status === "HELD"
                            ? "border-l-4 border-amber-400"
                            : ""
                      }
                      onClick={() => openDetail(row.disbursementId)}
                    >
                      <Td>
                        <div className="space-y-2">
                          <StatusPill tone={priority.tone}>{priority.label}</StatusPill>
                          <StatusPill tone={statusTone(row.status)}>
                            {formatEnumLabel(row.status)}
                          </StatusPill>
                        </div>
                      </Td>
                      <Td>
                        <div className="space-y-1">
                          <div className="font-semibold text-slate-900">{row.clientName}</div>
                          <div className="text-xs text-slate-500">{row.clientPhoneNumber}</div>
                          <div className="text-xs text-slate-500">
                            {row.loanId} · {row.disbursementId}
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <div className="space-y-1">
                          <div className="font-medium text-slate-900">
                            {formatCurrency(row.amount, row.currency)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {row.productName} · {Number(row.interestRate ?? 0) * 100}% APR
                          </div>
                          <div className="text-xs text-slate-500">{row.termDays} day term</div>
                        </div>
                      </Td>
                      <Td>
                        <div className="space-y-1">
                          <StatusPill tone={statusTone(row.kycStatus)}>{formatEnumLabel(row.kycStatus)}</StatusPill>
                          <div className="text-xs text-slate-500">
                            {row.kycReviewerName ?? "Unreviewed"}
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <div className="space-y-1">
                          <div className="font-medium text-slate-900">{formatEnumLabel(row.paymentMethod)}</div>
                          <div className="text-xs text-slate-500">{row.destinationAccountMasked}</div>
                        </div>
                      </Td>
                      <Td>
                        <div className="space-y-1 text-xs text-slate-500">
                          <div>{formatDateTime(row.createdAt)}</div>
                          <div>{row.region ?? "Region not set"}</div>
                        </div>
                      </Td>
                      <Td>
                        <div className={`text-sm font-semibold ${queueAgeClass(row.queueAgeMinutes)}`}>
                          {queueAgeText(row.queueAgeMinutes)}
                        </div>
                        {row.timeInProcessingMinutes ? (
                          <div className="text-xs text-slate-500">
                            {queueAgeText(row.timeInProcessingMinutes)} in processing
                          </div>
                        ) : null}
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-1.5">
                          {(row.riskFlags ?? []).length > 0 ? (
                            row.riskFlags.map((flag: string) => (
                              <StatusPill
                                key={flag}
                                tone={
                                  flag === "High Value" || flag === "Repeat Defaulter"
                                    ? "danger"
                                    : "warning"
                                }
                              >
                                {flag}
                              </StatusPill>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400">No flags</span>
                          )}
                        </div>
                      </Td>
                      <Td className="text-right">
                        <Button
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation()
                            openDetail(row.disbursementId)
                          }}
                        >
                          Review
                        </Button>
                      </Td>
                    </TableRow>
                  )
                })}
              </TableBody>
            </DataTable>
          </div>
        )}
      </ContentCard>

      <Drawer
        open={Boolean(selectedId)}
        onClose={() => updateParam("selected_disbursement_id", null)}
        title={detail?.disbursementId ?? "Disbursement Review"}
        subtitle="Review client, KYC, loan, and audit context before moving funds."
        widthClassName="w-[min(980px,100vw)]"
        footer={
          detail ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => openAction("approve", detail)}
                disabled={!canRunAction(detail, "approve")}
              >
                Approve & Disburse
              </Button>
              <Button
                variant="outline"
                onClick={() => openAction("hold", detail)}
                disabled={!canRunAction(detail, "hold")}
              >
                Hold / Flag
              </Button>
              <Button
                variant="danger"
                onClick={() => openAction("reject", detail)}
                disabled={!canRunAction(detail, "reject")}
              >
                Reject
              </Button>
              <Button
                variant="secondary"
                onClick={() => openAction("retry", detail)}
                disabled={!canRunAction(detail, "retry")}
              >
                Retry
              </Button>
              <Button
                variant="secondary"
                onClick={() => openAction("manual-complete", detail)}
                disabled={!canRunAction(detail, "manual-complete")}
              >
                Manual Complete
              </Button>
              <Button
                variant="danger"
                onClick={() => openAction("manual-fail", detail)}
                disabled={!canRunAction(detail, "manual-fail")}
              >
                Manual Fail
              </Button>
            </div>
          ) : null
        }
      >
        {detail ? (
          <div className="space-y-5">
            <MetricGrid columns="xl:grid-cols-4">
              <StatCard label="Status" value={formatEnumLabel(detail.status)} tone="blue" />
              <StatCard
                label="Amount"
                value={formatCurrency(detail.amount, detail.currency)}
                tone="emerald"
              />
              <StatCard
                label="Queue Age"
                value={queueAgeText(detail.queueAgeMinutes)}
                tone={(detail.queueAgeMinutes ?? 0) >= 120 ? "amber" : "slate"}
              />
              <StatCard
                label="Retry Count"
                value={detail.retryCount ?? 0}
                tone={(detail.retryCount ?? 0) > 0 ? "amber" : "slate"}
              />
            </MetricGrid>

            <ContentCard title="Client Summary" subtitle="Identity, KYC, and prior borrowing context.">
              <FieldList
                items={[
                  { label: "Client", value: detail.client.fullName, accent: true },
                  { label: "Phone", value: detail.client.phoneNumber },
                  { label: "Date of birth", value: formatDateTime(detail.client.dateOfBirth) },
                  { label: "Address", value: detail.client.physicalAddress ?? "-" },
                  { label: "KYC status", value: formatEnumLabel(detail.client.kycStatus) },
                  {
                    label: "KYC reviewer",
                    value: detail.client.kycReviewer?.fullName ?? "No reviewer",
                  },
                  {
                    label: "Prior loans",
                    value: detail.priorLoanStats?.priorLoanCount ?? 0,
                  },
                  {
                    label: "Prior defaults",
                    value: detail.priorLoanStats?.priorDefaultCount ?? 0,
                  },
                  {
                    label: "Repayment rate",
                    value:
                      detail.priorLoanStats?.onTimeRepaymentRate !== null
                        ? `${detail.priorLoanStats?.onTimeRepaymentRate}%`
                        : "No history",
                  },
                  {
                    label: "Risk score",
                    value: detail.client.riskScore ?? "Unscored",
                  },
                ]}
                columns={2}
              />
              <div className="mt-4 flex flex-wrap gap-2">
                {(detail.riskFlags ?? []).map((flag: string) => (
                  <StatusPill
                    key={flag}
                    tone={flag === "High Value" || flag === "Repeat Defaulter" ? "danger" : "warning"}
                  >
                    {flag}
                  </StatusPill>
                ))}
              </div>
            </ContentCard>

            <ContentCard title="Loan Terms" subtitle="Repayment and penalty terms that will become active after disbursement.">
              <FieldList
                items={[
                  { label: "Loan ID", value: detail.loan.loanId, accent: true },
                  { label: "Product", value: detail.loan.productName ?? detail.productName ?? "-" },
                  {
                    label: "Principal",
                    value: formatCurrency(detail.loan.principalAmount, detail.currency),
                  },
                  {
                    label: "Total repayable",
                    value: formatCurrency(detail.loan.totalRepayableAmount, detail.currency),
                  },
                  {
                    label: "Interest rate",
                    value: `${Number(detail.loan.interestRate ?? 0) * 100}%`,
                  },
                  { label: "Term", value: `${detail.loan.termDays} days` },
                  { label: "Due date", value: formatDateTime(detail.loan.dueDate) },
                  {
                    label: "Penalty cap",
                    value: `${Number(detail.productPenalties.penaltyCapRate ?? 0) * 100}% of principal`,
                  },
                ]}
              />
              <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
                <DataTable>
                  <TableHead>
                    <Th>Payment</Th>
                    <Th>Expected Date</Th>
                    <Th>Expected Amount</Th>
                  </TableHead>
                  <TableBody>
                    {(detail.repaymentSchedule ?? []).map((entry: any) => (
                      <TableRow key={entry.paymentNumber}>
                        <Td>{entry.paymentNumber}</Td>
                        <Td>{formatDateTime(entry.expectedDate)}</Td>
                        <Td>{formatCurrency(entry.expectedAmount, detail.currency)}</Td>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </div>
            </ContentCard>

            <ContentCard title="Disbursement Details" subtitle="Processing destination and operational notes.">
              <FieldList
                items={[
                  { label: "Method", value: formatEnumLabel(detail.paymentMethod) },
                  { label: "Destination", value: detail.destinationAccount, accent: true },
                  { label: "Currency", value: detail.currency },
                  { label: "Created", value: formatDateTime(detail.createdAt) },
                  { label: "Approved", value: formatDateTime(detail.approvedAt) },
                  { label: "Processed", value: formatDateTime(detail.processedAt) },
                  { label: "Completed", value: formatDateTime(detail.completedAt) },
                  { label: "Transaction Ref", value: detail.transactionRef ?? "-" },
                  { label: "Review Note", value: detail.reviewNote ?? "-" },
                  { label: "Hold Reason", value: detail.holdReason ?? "-" },
                  { label: "Rejection Reason", value: detail.rejectedReason ?? "-" },
                  { label: "Failure Reason", value: detail.failedReason ?? "-" },
                ]}
                columns={2}
              />
            </ContentCard>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <ContentCard title="Linked Tickets" subtitle="Open operational issues tied to this client or loan.">
                {(detail.linkedTickets ?? []).length === 0 ? (
                  <EmptyState
                    title="No linked tickets"
                    description="No tickets are currently attached to this client or loan."
                  />
                ) : (
                  <div className="space-y-3">
                    {detail.linkedTickets.map((ticket: any) => (
                      <div key={ticket.ticketId} className="rounded-xl border border-slate-200 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{ticket.subject}</p>
                            <p className="text-xs text-slate-500">{ticket.ticketId}</p>
                          </div>
                          <StatusPill tone={statusTone(ticket.status)}>{ticket.status}</StatusPill>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          {ticket.priority} · {ticket.assignedToName ?? "Unassigned"} · Updated{" "}
                          {formatDateTime(ticket.updatedAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ContentCard>

              <ContentCard title="Audit Feed" subtitle="Recent loan and disbursement audit events.">
                {(detail.auditSummary ?? []).length === 0 ? (
                  <EmptyState
                    title="No audit events"
                    description="No recent audit events were returned for this record."
                  />
                ) : (
                  <div className="space-y-3">
                    {detail.auditSummary.map((entry: any) => (
                      <div key={entry.auditId} className="rounded-xl border border-slate-200 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-900">
                            {formatEnumLabel(entry.actionType)}
                          </p>
                          <StatusPill
                            tone={
                              entry.severity === "HIGH_RISK"
                                ? "danger"
                                : entry.severity === "ELEVATED"
                                  ? "warning"
                                  : "neutral"
                            }
                          >
                            {entry.severity ?? "NORMAL"}
                          </StatusPill>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          {entry.description ?? "No description"} · {formatDateTime(entry.timestamp)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ContentCard>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Loading disbursement detail...</p>
        )}
      </Drawer>

      <Modal
        open={Boolean(actionState)}
        onClose={() => setActionState(null)}
        title={actionState ? actionTitle(actionState.action) : "Action"}
        description={actionState ? actionDescription(actionState.action) : undefined}
        widthClassName="max-w-2xl"
      >
        {actionState ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Client
                  </p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {actionState.detail.client?.fullName ?? actionState.detail.clientName}
                  </p>
                  <p className="text-sm text-slate-500">
                    {actionState.detail.loan?.loanId ?? actionState.detail.loanId}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Destination
                  </p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {actionState.detail.destinationAccount}
                  </p>
                  <p className="text-sm text-slate-500">
                    {formatCurrency(actionState.detail.amount, actionState.detail.currency)}
                  </p>
                </div>
              </div>
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Audit Note
              </span>
              <Textarea
                rows={4}
                placeholder="Reason for this action"
                value={auditNote}
                onChange={(event) => setAuditNote(event.target.value)}
              />
            </label>

            {actionState.action === "hold" || actionState.action === "reject" ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Reason
                </span>
                <Textarea
                  rows={3}
                  placeholder={
                    actionState.action === "hold"
                      ? "KYC concern, documentation incomplete, high risk, or other"
                      : "Capture the rejection reason"
                  }
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
            ) : null}

            {actionState.action === "manual-fail" ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Failure Reason
                </span>
                <Textarea
                  rows={3}
                  placeholder="Provider or operational failure detail"
                  value={failureReason}
                  onChange={(event) => setFailureReason(event.target.value)}
                />
              </label>
            ) : null}

            {actionState.action === "manual-complete" ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Transaction Reference
                </span>
                <Input
                  placeholder="Provider or manual settlement reference"
                  value={transactionRef}
                  onChange={(event) => setTransactionRef(event.target.value)}
                />
              </label>
            ) : null}

            {actionState.action === "approve" || actionState.action === "retry" ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Local Provider Simulation
                </span>
                <Select
                  value={simulateResult}
                  onChange={(event) => setSimulateResult(event.target.value)}
                >
                  <option value="PROCESSING">Send to processing</option>
                  <option value="COMPLETED">Complete immediately</option>
                  <option value="FAILED">Fail immediately</option>
                </Select>
              </label>
            ) : null}

            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button variant="ghost" onClick={() => setActionState(null)}>
                Cancel
              </Button>
              <Button
                variant={actionState.action === "reject" || actionState.action === "manual-fail" ? "danger-solid" : "primary"}
                disabled={
                  actionMutation.isPending ||
                  auditNote.trim().length < 5 ||
                  ((actionState.action === "hold" || actionState.action === "reject") &&
                    reason.trim().length < 3) ||
                  (actionState.action === "manual-fail" && failureReason.trim().length < 3)
                }
                onClick={() =>
                  actionMutation.mutate({
                    action: actionState.action,
                    disbursementId: actionState.disbursementId,
                  })
                }
              >
                {actionMutation.isPending ? "Submitting..." : actionTitle(actionState.action)}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
